import React, { ReactElement, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { action } from 'mobx';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { DefaultClosePopoverButton } from '../common-popover/default-close-popover-button';
import DefaultButton from '../default-button/default-button';
import { Component } from '../../../infrastructure/type-extensions';
import Text from '../../global/text/text';
import { FormField } from '../form/form-field/form-field';
import { addText, cancelText } from '../../../constants/common-labels.constants';

interface ISelectTextListProps {
    label: string | ReactElement;
    classname?: object;
    optionsList: string[];
    addOption: (value: string) => void;
    removeOption: (value: string) => void;
    getFieldErrorMessage: (text: string, isMandatory?: boolean, isAlphaNumeric?: boolean) => string;
    optionsInvalidErrorMessage?: string;
    minOptionsCount?: number | null;
    required?: boolean;
}

interface IFormContainerDerivedProps {
    isOpen: boolean;
}

export const makeAnotherAnswerFormInPopover = <T extends IFormContainerDerivedProps>(PopoverTriggerComponent: Component<T>) => {
    // we need to utilize context.consumer in order to identify 'isOpen' value, but usage of popover forcing ref forwarding
    // therefore utilize React.forwardRef to create HOC over the PopoverTriggerComponent, which would utilize context and forward ref
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <PopoverTriggerComponent {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });
    // T can not be casted to ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>
    // can't get the reason for this, but for now just cast targetComponentProps to any while passing to component with forwarded ref
    // tslint:disable:no-any
    return props => {

        const [inputValue, setInputValue] = useState('');

        const labelFieldError = props.getFieldErrorMessage(inputValue, false);

        const onApply = (): void => {
            if (!labelFieldError && inputValue) {
                props.onApply(inputValue);
                reset();
            }
        };
        const reset = (): void => setInputValue('');

        return (
            <CommonPopover
                className='oe-popup-layout__body--fixed-width event-link-form-in-popover'
                body={
                    <div className='p-20px'>
                        <input
                            className='oe-form-field__control'
                            value={inputValue}
                            onChange={(e => setInputValue(e.target.value))}
                            type='text'
                        />
                        {labelFieldError &&
                            <span className='oe-form-field__error'>
                                {<FormattedMessage
                                    id='oe.label.answer'
                                    description='Label text'
                                    defaultMessage='Answer {suffix}'
                                    values={{
                                        suffix: labelFieldError
                                    }}
                                />}
                            </span>
                        }
                    </div>
                }
                footer={
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultClosePopoverButton
                            text={addText()}
                            modifier={['fat', 'confirm-safe']}
                            onClick={onApply}
                            disabled={labelFieldError}
                        />
                        <DefaultClosePopoverButton
                            text={cancelText()}
                            onClick={()=>{
                                if (props?.onCancel) {
                                    props?.onCancel();
                                }
                                reset();
                            }}
                        />
                    </div>
                }
                position='left'
                align='start'
            >
                <ComponentWithForwardedRef {...props} />
            </CommonPopover>
            // tslint:enable:no-any
        );
    };
};


const OpenFormInPopOverBtn = makeAnotherAnswerFormInPopover(
    React.forwardRef((props: { isOpen: boolean }, ref: React.Ref<HTMLInputElement>) => {
        return <DefaultButton
            ref={ref}
            text={<FormattedMessage id='oe.triageQuestion.addAnswer' description='button label' defaultMessage='+ Add Answer' />}
            className={classnames('default-button__add-button', { 'active': props.isOpen })}
        />;
    })
);

const SelectTextList = (props: ISelectTextListProps) => {
    const {
        label,
        optionsList,
        addOption,
        removeOption,
        getFieldErrorMessage,
        minOptionsCount = null,
        optionsInvalidErrorMessage
    } = props;

    return (
        <div>
            <FormField label={label}>
                {optionsList.length > 0 && optionsList.map((value, i) => (
                    <div key={i} className='organization__row'>
                        <Text tag='div' message={value} withEllipsis />
                        <Text
                            tag='label'
                            onClick={() => removeOption(value)}
                            className='organization__remove-label'
                            id='oe.common.remove'
                            description='Button label'
                            message='Remove'
                        />
                    </div>
                ))}
                {
                    <OpenFormInPopOverBtn
                        onApply={action((value: string) => addOption(value))}
                        getFieldErrorMessage={getFieldErrorMessage}
                    />
                }
                {optionsInvalidErrorMessage &&
                    <span className='oe-form-field__error'>
                        <FormattedMessage
                            id='oe.error.invalidOptions'
                            description='Label text'
                            defaultMessage={optionsInvalidErrorMessage}
                        />
                    </span>
                }
                {!!minOptionsCount && optionsList.length < minOptionsCount &&
                    <span className='oe-form-field__error'>
                        <FormattedMessage
                            id='oe.error.minimumNumberIs'
                            description='Label text'
                            defaultMessage='Minimum number is {suffix}'
                            values={{
                                suffix: minOptionsCount
                            }}
                        />
                    </span>
                }
                {props.required && optionsList.length === 0 &&
                    <span className='oe-form-field__error'>
                        <FormattedMessage
                            id='oe.error.optionsRequired'
                            description='Label text'
                            defaultMessage='At least one Answer Option is required.'
                        />
                    </span>
                }
            </FormField>
        </div>
    );
};

export default SelectTextList;
